import type React from 'react'
import { createContext, useContext, useMemo } from 'react'

import { useRepeatableTasks } from '@/modules/tasks/query/repeatable-task-query'
import { useLists } from '@/modules/tasks/query/task-list-query'
import { useTasks } from '@/modules/tasks/query/task-query'
import type { RepeatableTask } from '@/modules/tasks/service/repeatable-task-service'
import type { List } from '@/modules/tasks/service/task-list-service'
import type { Task } from '@/modules/tasks/service/task-service'

interface TaskQueryContextProps {
  tasks: Task[]
  repeatableTasks: RepeatableTask[]
  lists: List[]
  isFetching: boolean
  invalidateTasks: () => Promise<unknown> | void
  invalidateLists: () => Promise<unknown> | void
}

const TaskQueryContext = createContext<TaskQueryContextProps | undefined>(
  undefined,
)

export type TaskQueryProviderProps = React.PropsWithChildren<{
  enabled?: boolean
}>

export const TaskQueryProvider = ({
  children,
  enabled,
}: TaskQueryProviderProps) => {
  const options = enabled !== undefined ? { enabled } : undefined
  const tasks = useTasks(options)
  const repeatableTasks = useRepeatableTasks(options)
  const lists = useLists(options)
  const taskSorter = (a: Task, b: Task) => {
    return a.completed == b.completed ? 0 : a.completed ? 1 : -1
  }
  const context = useMemo<TaskQueryContextProps>(
    () => ({
      tasks: tasks.data?.sort(taskSorter) ?? [],
      repeatableTasks: repeatableTasks.data ?? [],
      lists: lists.data ?? [],
      isFetching:
        tasks.isLoading ||
        tasks.isIdle ||
        lists.isLoading ||
        lists.isIdle ||
        repeatableTasks.isLoading ||
        repeatableTasks.isIdle,
      invalidateTasks: () =>
        Promise.allSettled([tasks.refetch(), repeatableTasks.refetch()]),
      invalidateLists: () => lists.refetch(),
    }),
    [lists, tasks, repeatableTasks],
  )

  return (
    <TaskQueryContext.Provider value={context}>
      {children}
    </TaskQueryContext.Provider>
  )
}

export function useTaskQuery() {
  const context = useContext(TaskQueryContext)

  if (!context) {
    throw new Error('useTaskQuery must be used within TaskQueryProvider')
  }

  return context
}
